<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Categories</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Categories</div>
      <div>You can add, edit, and delete all of Categories from this page.</div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-filter-wrapper">
          <div class="admin-search-container">
            <input
              type="text"
              class="admin-search-input"
              placeholder="Search..."
              v-model="searchQuery"
              @input="handleSearchChange"
            />
            <img
              src="/images/maker-cart/union.svg"
              alt="Search"
              class="admin-search-icon"
            />
          </div>
          <div class="admin-filter-container">
            <div>
              <SingleSelect
                placeholder="Sort By"
                v-model="currentSort"
                :options="sortOptions"
                prefix="Sort: "
                @change="handleSortChange"
              />
            </div>
          </div>
        </div>
        <div class="d-flex ga-3">
          <BaseButton
            variant="secondary"
            class="mx-2 my-md-auto"
            @click="handleAction({ type: 'add' })"
          >
            <div class="d-flex align-center ga-1">
              <img
                src="/images/icon/plus-circle.svg"
                width="20"
                height="20"
                alt="download-icon"
              />
              <span class="admin-button-text">Add Categories</span>
            </div>
          </BaseButton>
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="true"
        :is-edit-action="true"
        :is-delete-action="true"
        :is-view-action="true"
        @action="handleAction"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      />
    </div>
  </div>

  <ConfirmationModal
    v-model="showModalDelete"
    variant="danger"
    title="Remove Category"
    message="Are you sure want to remove this category?"
    confirm-text="Confirm"
    cancel-text="Back"
    :loading="isLoading"
    @confirm="remove(itemId)"
    @cancel="updateVisibilityModalDelete"
  />
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, inject, watch } from "vue";
import CategoryAdminService from "@/services/categoryAdminService";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import TableData from "@/components/common/TableData.vue";
import { debounce } from "lodash";
import { useRouter } from "vue-router";
import BaseButton from "@/components/common/BaseButton.vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";
import { capitalize } from "@/utils";

const showModalDelete = ref(false);
const isLoading = ref(false);
const itemId = ref(null);
const tableLoading = ref(true);
const searchQuery = ref("");
const currentStatus = ref(""); // Menyimpan status pengguna saat ini
const currentSort = ref("newest"); // Current sort option
const notify = inject("notify");

const router = useRouter();

const data = ref({
  message: "",
  error: null,
  data: {
    search: "",
    page: 0,
    limit: 0,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const sortOptions = ref([
  { text: "Newest", value: "newest" },
  { text: "Oldest", value: "oldest" },
  { text: "Title A-Z", value: "title-a-z" },
  { text: "Title Z-A", value: "title-z-a" },
]);

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  sort_by: "id",
  order: "desc",
});

// Setup URL query sync with filters
useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

// Define table headers
const headers = ref([
  { text: "Title", value: "title", sortable: false },
  { text: "Name", value: "name", sortable: false },
  { text: "Module", value: "category_for_display", sortable: false },
]);

// Computed property to transform data for the table
const tableItems = computed(() => {
  return data.value.data.rows.map((item) => {
    // Format category_for field
    let categoryForDisplay = "-";
    if (
      item.category_for &&
      Array.isArray(item.category_for) &&
      item.category_for.length > 0
    ) {
      categoryForDisplay = item.category_for
        .map((item) => capitalize(item))
        .join(", ");
    }

    return {
      ...item,
      id: item.id, // Ensure each item has an id for the key
      category_for_display: categoryForDisplay,
    };
  });
});

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Sync sort options with filters
watch(
  [() => filters.value.sort_by, () => filters.value.order],
  ([sortBy, order]) => {
    if (sortBy === "id" && order === "desc") {
      currentSort.value = "newest";
    } else if (sortBy === "id" && order === "asc") {
      currentSort.value = "oldest";
    } else if (sortBy === "title" && order === "asc") {
      currentSort.value = "title-a-z";
    } else if (sortBy === "title" && order === "desc") {
      currentSort.value = "title-z-a";
    }
  },
  { immediate: true }
);

onMounted(async () => {
  // Initial URL params are handled by useUrlQuerySync
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const handleSortChange = () => {
  switch (currentSort.value) {
    case "newest":
      filters.value.sort_by = "id";
      filters.value.order = "desc";
      break;
    case "oldest":
      filters.value.sort_by = "id";
      filters.value.order = "asc";
      break;
    case "title-a-z":
      filters.value.sort_by = "title";
      filters.value.order = "asc";
      break;
    case "title-z-a":
      filters.value.sort_by = "title";
      filters.value.order = "desc";
      break;
    default:
      filters.value.sort_by = "title";
      filters.value.order = "asc";
  }
  filters.value.page = 1;
  getAll();
};

const handleAction = (action) => {
  if (action.type === "edit") {
    router.push({
      name: "CategoriesEditPage",
      params: { id: action.item.id },
    });
  } else if (action.type === "delete") {
    itemId.value = action.item.id;
    showModalDelete.value = true;
  } else if (action.type === "view") {
    router.push({
      name: "TermsListPage",
      params: { id: action.item.id },
    });
  } else if (action.type === "add") {
    router.push({
      name: "CategoriesFormPage",
    });
  }
};

const getAll = async () => {
  try {
    tableLoading.value = true;

    const params = { ...filters.value };

    if (currentStatus.value === "active") {
      params.status = "true";
      params.approved = null; // Reset approved when status is active
    } else if (currentStatus.value === "pending") {
      params.status = "true";
      params.approved = "false";
    } else if (currentStatus.value === "blocked") {
      params.status = "false";
      params.approved = "false";
    } else {
      params.status = null;
      params.approved = null;
    }

    const response = await CategoryAdminService.getAll(params);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching categories:", error.message);
    // Optionally, reset data or show an error message
    data.value.data.rows = [];
    data.value.data.total_pages = 0;
  } finally {
    tableLoading.value = false;
  }
};

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

const remove = async (id) => {
  isLoading.value = true;
  try {
    await CategoryAdminService.remove(id);
    isLoading.value = false;
    showModalDelete.value = false;
    getAll(); // Refresh the list using the new getAll without params
    notify.success("Category deleted successfully", "Success");
  } catch (error) {
    isLoading.value = false;
    console.error(error.message);
    notify.error("Failed to delete category", "Error");
  }
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    // Memindahkan toolContainer di bawah admin-flex-container
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    // Memindahkan toolContainer kembali ke dalam admin-flex-container
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};
</script>

<style scoped>
.admin-search-filter-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-filter-container {
  min-width: 200px;
  width: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

@media (max-width: 768px) {
  .admin-search-filter-wrapper {
    width: 100%;
  }

  .admin-filter-container {
    width: 100%;
  }
}
</style>
